import { useEffect, useState } from 'react';

import { Button, Checkbox, Col, Divider, Form, Input, Modal, Row } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';

import { KitCampaign } from 'models/KitCampaign';
import { TiendaPerfectaDistributor } from 'models/TiendaPerfecta';

import { RoleFormPayload } from '../types';
import KitCampaignsFields from './KitCampaignsFields';
import TiendaPerfectaAccessFields, {
  EMPTY_TIENDA_PERFECTA_ACCESS,
  TiendaPerfectaAccessValue,
  buildTiendaPerfectaPayload,
} from './TiendaPerfectaAccessFields';

interface NewRoleModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (payload: RoleFormPayload) => void;
  campaigns: KitCampaign[];
  kitIntegrationEnabled: boolean;
  tiendaPerfectaIntegrationEnabled: boolean;
  tiendaPerfectaDistributors: TiendaPerfectaDistributor[];
  tiendaPerfectaDistributorsLoading: boolean;
}

function NewRoleModal({
  isOpen,
  handleModalCancel,
  submit,
  campaigns,
  kitIntegrationEnabled,
  tiendaPerfectaIntegrationEnabled,
  tiendaPerfectaDistributors,
  tiendaPerfectaDistributorsLoading,
}: NewRoleModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [kitCampaignsFullAccess, setKitCampaignsFullAccess] = useState(false);
  const [tiendaPerfectaAccess, setTiendaPerfectaAccess] =
    useState<TiendaPerfectaAccessValue>(EMPTY_TIENDA_PERFECTA_ACCESS);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setKitCampaignsFullAccess(false);
      setTiendaPerfectaAccess(EMPTY_TIENDA_PERFECTA_ACCESS);
    }
  }, [isOpen]);

  const handleFullAccessChange = (checked: boolean) => {
    setKitCampaignsFullAccess(checked);
    if (checked) {
      form.setFieldValue('campaignIds', []);
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit({
      name: values.name.trim(),
      description: (values.description || '').trim() || null,
      isDefaultRoleForStartedAccount:
        values.isDefaultRoleForStartedAccount === undefined
          ? false
          : values.isDefaultRoleForStartedAccount,
      kitCampaignsFullAccessEnabled: kitCampaignsFullAccess,
      campaignIds:
        kitIntegrationEnabled && !kitCampaignsFullAccess
          ? (values.campaignIds ?? []).filter(
              (v: number | string) => typeof v === 'number'
            )
          : [],
      ...buildTiendaPerfectaPayload(
        tiendaPerfectaAccess,
        tiendaPerfectaIntegrationEnabled
      ),
    });

    setLoading(false);
  };

  return (
    <Modal
      title={t('scenes.settings.roles.components.new_role_modal.title')}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={560}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="name"
              label={t(
                'scenes.settings.roles.components.new_role_modal.labels.name'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.settings.roles.components.new_role_modal.validations.name.required'
                  ),
                },
              ]}
            >
              <Input
                type="text"
                disabled={loading}
                placeholder={t(
                  'scenes.settings.roles.components.new_role_modal.placeholders.name'
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="description"
              label={t(
                'scenes.settings.roles.components.new_role_modal.labels.description'
              )}
            >
              <TextArea
                placeholder={t(
                  'scenes.settings.roles.components.new_role_modal.placeholders.description'
                )}
                rows={4}
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        {kitIntegrationEnabled && (
          <KitCampaignsFields
            campaigns={campaigns}
            fullAccess={kitCampaignsFullAccess}
            onFullAccessChange={handleFullAccessChange}
            disabled={loading}
          />
        )}

        {tiendaPerfectaIntegrationEnabled && (
          <TiendaPerfectaAccessFields
            value={tiendaPerfectaAccess}
            onChange={setTiendaPerfectaAccess}
            distributors={tiendaPerfectaDistributors}
            distributorsLoading={tiendaPerfectaDistributorsLoading}
            disabled={loading}
          />
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="isDefaultRoleForStartedAccount"
              valuePropName="checked"
            >
              <Checkbox disabled={loading}>
                {t(
                  'scenes.settings.roles.components.new_role_modal.checkbox.default_role_text'
                )}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.settings.roles.components.new_role_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default NewRoleModal;
